/**
 * The workspace page: the wall (default) or the old canvas, by this device's
 * preference (Part 04 Task 9, Part 06 decisions 3, 6 and 9).
 *
 * - Wall path: `WallView` is mounted in every mode and shows only where
 *   `wallVisibleHere()`, so it stays hidden while compact focus shows
 *   `canvas-area`. `WallFrost` under it stays mounted too and fades the
 *   frost in with the Workspace (plan 11 A3). `FocusOverlay` beside it
 *   draws focus over the wall and compact focus over the desktop (Part 05
 *   Task 6). React Flow is never mounted or loaded here.
 * - Canvas path: the lazy `LegacyCanvasView` in every mode, as before; focus
 *   is today's canvas focus.
 * - Desktop floating windows (`FloatingFrameLayer`) portal above both paths
 *   in every mode.
 *
 * Both views share one isolated layer root inside `canvas-area` (Part 06
 * decision 3): the view < `FocusOverlay` < the agent cursors < the summon
 * bar. Its `isolate` keeps those z-indexes below the header and panels.
 *
 * A mode change swaps views without reloading frames; switching the
 * preference reloads them (`desktop/use-owner-connection.ts`).
 *
 * The owner's backend WebSocket and REST frame load live in
 * use-owner-connection.ts (mounted headless in DesktopShell for desktop).
 * Here we mount that same hook with { enabled: !isTauri() } so the web SPA —
 * which has no DesktopShell — still establishes exactly one connection.
 */

import { lazy, Suspense, useEffect, useRef } from "react";
import { LayoutProvider } from "@/components/layout/layout-provider";
import { MasterLayout } from "@/components/layout/master-layout";
import { FocusOverlay } from "@/components/focus/focus-overlay";
import { WorkspaceInteractionLayers } from "@/components/layout/workspace-interaction-layers";
import { WallFrost } from "@/components/wall/wall-frost";
import { WallView } from "@/components/wall/wall-view";
import { useProjectStore } from "@/stores/project-store";
import { useCompanionStore } from "@/stores/companion-store";
import type {
  AvailableRuntime,
  AvailableModelsMap,
} from "@/stores/companion-store";
import { useActivityStore } from "@/stores/activity-store";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { useTasksStore } from "@/stores/tasks-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useWorkspaceViewStore } from "@/stores/workspace-view-store";
import { getTokenStatus } from "@/api/companion";
import { useAwaitingInputTitle } from "@/hooks/use-awaiting-input-title";
import { FloatingFrameLayer } from "@/desktop/floating-frame-layer";
import { useOwnerConnection } from "@/desktop/use-owner-connection";
import { useServerUnreachableToast } from "@/desktop/connection-health/use-server-unreachable-toast";
import { isTauri } from "@/lib/is-tauri";
import { startFocusModeTransitions } from "@/lib/focus/focus-mode-transitions";
import { useWallVisibleHere } from "@/lib/wall-visibility";
import { revealWallCard } from "@/lib/wall-reveal";
import { useT } from "@/i18n";
import { pagesCanvasMessages } from "@/i18n/messages/pages-canvas";

const LegacyCanvasView = lazy(() =>
  import("@/pages/canvas-page").then((module) => ({
    default: module.LegacyCanvasView,
  })),
);

/** Closing focus over the wall gives keyboard focus back to the card. */
function returnFocusToCard(frameId: string): void {
  revealWallCard(frameId, { focus: true });
}

export function WorkspacePage() {
  // Web SPA: own the backend connection here (no DesktopShell). In Tauri this
  // is inert — DesktopShell mounts the live owner connection at bootstrap.
  useOwnerConnection({ enabled: !isTauri() });
  useServerUnreachableToast({ enabled: !isTauri() });

  const currentProjectId = useProjectStore((s) => s.currentProject?.id);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);

  const setCompanionStatus = useCompanionStore((s) => s.setStatus);

  const fetchActivity = useActivityStore((s) => s.fetchActivity);
  const clearActivity = useActivityStore((s) => s.clear);
  const fetchSummary = useNotificationStore((s) => s.fetchSummary);
  const fetchTasks = useTasksStore((s) => s.fetchTasks);

  const onWall = useWorkspaceViewStore((s) => s.preferred === "wall");
  const wallVisible = useWallVisibleHere();
  const layerRootRef = useRef<HTMLDivElement>(null);

  useAwaitingInputTitle();

  useEffect(() => startFocusModeTransitions(), []);

  // Load projects and initial notification count on mount.
  useEffect(() => {
    void fetchProjects();
    void fetchSummary();
  }, [fetchProjects, fetchSummary]);

  // Fetch companion token status once on mount so we know whether the user has
  // ever installed the Agent Runner (gates the "offline" banner).
  useEffect(() => {
    let cancelled = false;
    getTokenStatus()
      .then((info) => {
        if (cancelled) return;
        useCompanionStore.getState().setHasToken(!!info.has_token);
        if (info.has_token && info.companion_online) {
          setCompanionStatus("connected");
        }
        if (info.available_runtimes) {
          useCompanionStore
            .getState()
            .setAvailableRuntimes(
              info.available_runtimes as AvailableRuntime[],
              info.default_runtime ?? "claude_cli",
            );
        }
        if (info.available_models) {
          useCompanionStore
            .getState()
            .setAvailableModels(info.available_models as AvailableModelsMap);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [setCompanionStatus]);

  const previousTasksProject = useRef(currentProjectId);
  // Load project-scoped activity and tasks when the selected project changes.
  useEffect(() => {
    if (previousTasksProject.current !== currentProjectId) {
      useTaskPocketStore.getState().clearForProjectChange();
      useTasksStore.getState().clearBoards();
      previousTasksProject.current = currentProjectId;
    }
    if (currentProjectId) {
      void fetchActivity(currentProjectId);
      void fetchTasks();
    } else {
      clearActivity();
    }
  }, [currentProjectId, fetchActivity, fetchTasks, clearActivity]);

  return (
    <LayoutProvider>
      <MasterLayout>
        {currentProjectId ? (
          <>
            <div
              ref={layerRootRef}
              className="relative isolate h-full w-full"
              data-focus-surface={onWall || undefined}
              data-testid="workspace-layer-root"
            >
              {onWall ? (
                <>
                  <WallFrost />
                  <WallView
                    key={currentProjectId}
                    projectId={currentProjectId}
                    visible={wallVisible}
                  />
                  <FocusOverlay onReturnFocus={returnFocusToCard} />
                </>
              ) : (
                <Suspense fallback={null}>
                  <LegacyCanvasView />
                </Suspense>
              )}
              <WorkspaceInteractionLayers rootRef={layerRootRef} />
            </div>
            {isTauri() ? <FloatingFrameLayer /> : null}
          </>
        ) : (
          <EmptyState />
        )}
      </MasterLayout>
    </LayoutProvider>
  );
}

function EmptyState() {
  const t = useT(pagesCanvasMessages);
  return (
    <div
      className="flex h-full flex-col items-center justify-center"
      data-testid="canvas-empty-state"
    >
      <h2 className="text-appkit-large-title font-bold text-primary">
        {t("welcome")}
      </h2>
      <p className="mt-2 text-appkit-subhead text-secondary">
        {t("emptyHint")}
      </p>
    </div>
  );
}
